import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, CheckCircle, AlertCircle, Info, Copy, RefreshCw } from 'lucide-react'
import { AppleAccount } from '../../data/appleAccounts'

// 扩展window对象类型
declare global {
  interface Window {
    [key: string]: any
  }
}

interface Step {
  id: number
  title: string
  description: string
  details: string[]
  icon: React.ReactNode
  isExpanded: boolean
  isImportant?: boolean
}

const MigrationSteps: React.FC = () => {
  const [appleAccounts, setAppleAccounts] = useState<AppleAccount[]>([])
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false)
  const [accountsError, setAccountsError] = useState<string>('')

  const fetchAppleAccounts = async () => {
    setIsLoadingAccounts(true)
    setAccountsError('')
    
    // 使用Cloudflare Pages Functions代理
    try {
      const response = await fetch('/api-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.code === 1 && data.data && Array.isArray(data.data)) {
          setAppleAccounts(data.data)
          setIsLoadingAccounts(false)
          return
        } else {
          console.log('代理返回数据格式错误:', data)
          setAccountsError('代理返回数据格式错误')
        }
      } else {
        console.log('代理请求失败，状态码:', response.status)
        setAccountsError(`代理请求失败: ${response.status}`)
      }
    } catch (error) {
      console.log('代理API请求失败:', error)
      setAccountsError('代理API请求失败: ' + (error instanceof Error ? error.message : String(error)))
    }
    
    setIsLoadingAccounts(false)
  }



  useEffect(() => {
    fetchAppleAccounts()
  }, [])

  const [copySuccess, setCopySuccess] = useState<string>('')

  const copyToClipboard = (account: AppleAccount, type: 'appleid' | 'password' | 'both') => {
    let textToCopy = ''
    if (type === 'appleid') {
      textToCopy = account.appleid
    } else if (type === 'password') {
      textToCopy = account.password
    } else {
      textToCopy = `Apple ID: ${account.appleid}\n密码: ${account.password}`
    }
    
    navigator.clipboard.writeText(textToCopy).then(() => {
      setCopySuccess('已复制到剪贴板')
      setTimeout(() => setCopySuccess(''), 2000)
    }).catch(() => {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = textToCopy
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopySuccess('已复制到剪贴板')
      setTimeout(() => setCopySuccess(''), 2000)
    })
  }

  const [steps, setSteps] = useState<Step[]>([
    {
      id: 1,
      title: "重要提醒",
      description: "海外稳定版本迁移过程中请勿卸载'漫时光'",
      details: [
        "在整个迁移过程中，请保持漫时光App处于安装状态",
        "不要卸载或删除现有的漫时光App",
        "这样可以确保您的所有数据都不会丢失",
        "包括漫画收藏、阅读进度、设置等所有信息"
      ],
      icon: <AlertCircle className="w-6 h-6 text-orange-500" />,
      isExpanded: false,
      isImportant: true
    },
    {
      id: 2,
      title: "切换App Store区域",
      description: "使用外区Apple账号登录外区App Store",
      details: [
        "⚠️ 重要安全提醒：提供的Apple ID仅限在App Store中登录使用",
        "🚫 严禁在手机「设置」中登录这些账号，否则可能导致手机变砖",
        "✅ 正确操作：只在App Store应用中登录外区账号",
        "📱 操作步骤：打开App Store → 点击头像 → 退出当前账号 → 登录外区账号",
        "🔄 下载完成后：请及时退出外区账号，重新登录您的个人账号",
        "⚡ 账号信息如下（点击可复制）："
      ],
      icon: <Info className="w-6 h-6 text-blue-500" />,
      isExpanded: false
    },
    {
      id: 3,
      title: "下载外区版本",
      description: "在外区App Store搜索并下载漫时光",
      details: [
        "打开外区App Store",
        "搜索'漫时光'即可找到外区稳定版",
        "确认版本信息后点击下载安装",
        "等待安装完成",
        "由于全程未卸载原App，所有数据都会自动保留"
      ],
      icon: <CheckCircle className="w-6 h-6 text-green-500" />,
      isExpanded: false
    }
  ])

  const toggleStep = (stepId: number) => {
    setSteps(steps.map(step => 
      step.id === stepId 
        ? { ...step, isExpanded: !step.isExpanded }
        : step
    ))
  }

  return (
    <section id="migration-steps" className="py-16 px-4 bg-gray-50">
      {/* 复制成功提示 */}
      {copySuccess && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg"
        >
          {copySuccess}
        </motion.div>
      )}
      
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            详细迁移步骤
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            请按照以下步骤完成从中国区App Store到外区App Store的迁移。
            每个步骤都经过详细测试，确保数据安全和功能完整。
          </p>
        </motion.div>

        <div className="space-y-4">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`step-card ${step.isImportant ? 'ring-2 ring-orange-200' : ''}`}
            >
              <motion.button
                className="w-full flex items-center justify-between p-4"
                onClick={() => toggleStep(step.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0">
                    {step.icon}
                  </div>
                  <div className="text-left">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {step.title}
                      </h3>
                      {step.isImportant && (
                        <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded-full">
                          重要
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {step.description}
                    </p>
                  </div>
                </div>
                <motion.div
                  animate={{ rotate: step.isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {step.isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </motion.div>
              </motion.button>

              <AnimatePresence>
                {step.isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4">
                                             <div className="border-t border-gray-200 pt-4">
                         <h4 className="text-sm font-medium text-gray-700 mb-3">
                           详细操作步骤：
                         </h4>
                         <ol className="space-y-2">
                           {step.details.map((detail, detailIndex) => (
                             <motion.li
                               key={detailIndex}
                               initial={{ opacity: 0, x: -20 }}
                               animate={{ opacity: 1, x: 0 }}
                               transition={{ delay: detailIndex * 0.1 }}
                               className="flex items-start space-x-3 text-sm text-gray-600"
                             >
                               <span className="flex-shrink-0 w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xs font-medium">
                                 {detailIndex + 1}
                               </span>
                               <span className="leading-relaxed">{detail}</span>
                             </motion.li>
                           ))}
                         </ol>
                         
                         {/* Apple账号显示区域 */}
                         {step.id === 2 && (
                           <motion.div
                             initial={{ opacity: 0, y: 20 }}
                             animate={{ opacity: 1, y: 0 }}
                             transition={{ delay: 0.5 }}
                             className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4"
                           >
                             {/* 安全警告 */}
                             <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                               <div className="flex items-center space-x-2 mb-2">
                                 <AlertCircle className="w-4 h-4 text-red-500" />
                                 <h6 className="text-sm font-bold text-red-800">⚠️ 安全警告</h6>
                               </div>
                               <p className="text-xs text-red-700">
                                 <strong>仅限在App Store中登录！</strong><br/>
                                 严禁在手机「设置」中登录这些账号，否则可能导致手机变砖，后果自负！
                               </p>
                             </div>
                             
                             <div className="flex items-center justify-between mb-3">
                               <h5 className="text-sm font-semibold text-blue-800">
                                 外区Apple账号
                               </h5>
                               <motion.button
                                 onClick={fetchAppleAccounts}
                                 disabled={isLoadingAccounts}
                                 className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                 whileHover={{ scale: 1.05 }}
                                 whileTap={{ scale: 0.95 }}
                               >
                                 <RefreshCw className={`w-3 h-3 ${isLoadingAccounts ? 'animate-spin' : ''}`} />
                                 <span>刷新</span>
                               </motion.button>
                             </div>
                             
                             {isLoadingAccounts ? (
                               <div className="text-sm text-blue-600">正在获取账号...</div>
                             ) : accountsError ? (
                               <div className="text-sm text-red-600">{accountsError}</div>
                                                           ) : (
                                <div className="space-y-3">
                                  {appleAccounts.map((account, index) => (
                                    <motion.div
                                      key={index}
                                      initial={{ opacity: 0, x: -10 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: 0.6 + index * 0.1 }}
                                      className="bg-white rounded-lg p-4 border border-blue-200 shadow-sm"
                                    >
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="text-sm font-semibold text-gray-900">
                                          Apple ID {index + 1}
                                        </div>
                                        <motion.button
                                          onClick={() => copyToClipboard(account, 'both')}
                                          className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                        >
                                          <Copy className="w-3 h-3" />
                                          <span>复制全部</span>
                                        </motion.button>
                                      </div>
                                      
                                      <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="text-xs text-gray-500 mb-1">Apple ID</div>
                                            <div className="text-sm text-gray-900 break-all font-mono">
                                              {account.appleid}
                                            </div>
                                          </div>
                                          <motion.button
                                            onClick={() => copyToClipboard(account, 'appleid')}
                                            className="ml-2 p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                          >
                                            <Copy className="w-3 h-3" />
                                          </motion.button>
                                        </div>
                                        
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="text-xs text-gray-500 mb-1">密码</div>
                                            <div className="text-sm text-gray-900 font-mono">
                                              **********
                                            </div>
                                          </div>
                                          <motion.button
                                            onClick={() => copyToClipboard(account, 'password')}
                                            className="ml-2 p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                            whileHover={{ scale: 1.1 }}
                                            whileTap={{ scale: 0.9 }}
                                          >
                                            <Copy className="w-3 h-3" />
                                          </motion.button>
                                        </div>
                                      </div>
                                    </motion.div>
                                  ))}
                                </div>
                              )}
                             
                             <div className="mt-3 text-xs text-blue-600">
                               点击复制按钮可复制账号信息到剪贴板
                             </div>
                           </motion.div>
                         )}
                       </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* 注意事项 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="mt-12 bg-green-50 border border-green-200 rounded-2xl p-6"
        >
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                数据安全保障
              </h3>
              <ul className="space-y-2 text-sm text-green-700">
                <li>• 全程不卸载原App，所有数据都会自动保留</li>
                <li>• 包括漫画收藏、阅读进度、设置等所有信息</li>
                <li>• 无需手动备份和恢复数据</li>
                <li>• 迁移过程简单安全，无数据丢失风险</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* 回到顶部按钮 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-8 text-center"
        >
          <motion.button
            className="btn-secondary flex items-center justify-center space-x-2 mx-auto"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              window.scrollTo({ 
                top: 0, 
                behavior: 'smooth' 
              });
            }}
          >
            <span>回到顶部</span>
          </motion.button>
        </motion.div>
      </div>
    </section>
  )
}

export default MigrationSteps
